import { z } from "zod";
import { LessonSchema, SessionSchema } from "../domain";
import { LearnerProfileSchema } from "./profile-contracts";

export { ProfileSchema } from "./profile-contracts";

export const CreateSessionSchema = z
  .object({
    mode: z.enum(["hybrid", "all-live"]),
    runtime: z.enum(["rehearsal", "live"]),
  })
  .strict();
export const QuestionSchema = z
  .object({
    question: z.string().trim().min(1).max(4000),
    idempotencyKey: z.string().min(1).max(180),
  })
  .strict();
export const ReadinessSchema = z.object({
  live: z.object({
    available: z.boolean(),
    message: z.string(),
    blockers: z.array(z.string()),
    /** Profile routes whose vendor credentials and asset are configured on this deployment. */
    routes: z.array(z.string()).default([]),
    /**
     * Whether the published release carries rendered clips. Without them a hybrid lesson is a
     * still portrait, so the classroom opens on all-live instead.
     */
    coursePackVideo: z.boolean().default(false),
  }),
  rehearsal: z.object({ available: z.boolean(), label: z.string() }),
});
export const RunwayVariantSchema = z
  .object({
    /** Stable learner-facing visual choice. It is deliberately separate from providerPath. */
    presentation: z.enum(["animated", "photo-real"]),
    /** Public profile id accepted by the live-session route; root uses the existing root link. */
    profileId: z.string().min(1),
    label: z.string().min(1).max(120),
    portraitUrl: z.string().nullable(),
    available: z.boolean(),
    reason: z.string().nullable(),
    modes: z.object({ allLive: z.boolean(), hybrid: z.boolean() }).readonly(),
  })
  .readonly();
export const DemoResponseSchema = z.object({
  lesson: LessonSchema,
  profile: LearnerProfileSchema,
  readiness: ReadinessSchema,
  variants: z.array(RunwayVariantSchema).default([]),
});
export const SessionPinsSchema = z
  .object({
    scope: z.literal("public-demo"),
    profileVersion: z.string(),
    scriptVersion: z.string(),
    voiceId: z.string(),
    coursePackVersion: z.null(),
    modelPolicyVersion: z.literal("text-rehearsal-v1"),
    providerPath: z.literal("local-preview"),
    mediaRoom: z.null(),
  })
  .readonly();
export const SessionResponseSchema = z.object({
  pins: SessionPinsSchema,
  session: SessionSchema,
  runtime: z.literal("rehearsal"),
  expiresAt: z.number(),
});
export const SessionCreatedResponseSchema = SessionResponseSchema.extend({ token: z.string() });
export const SessionMutationResponseSchema = SessionResponseSchema.extend({
  accepted: z.boolean(),
  reason: z.string(),
  effects: z.array(
    z.object({
      type: z.enum(["cancel-output", "clear-preroll", "release-resources"]),
      generationEpoch: z.number(),
      bridgeFence: z.number(),
    }),
  ),
});
export type CreateSessionInput = z.infer<typeof CreateSessionSchema>;
